/**
 * The card grid, reorderable by dragging - with a keyboard route to the same thing.
 *
 * Native HTML5 drag and drop, no library. The whole interaction is "pick up one tile,
 * drop it on another", which is the one case the native API is actually good at, and a
 * drag-and-drop dependency is a large amount of code and lifecycle to own for it.
 *
 * Three decisions worth knowing:
 *
 * **The handle is the drag source; the card is only a drop target.** Marking the whole
 * card draggable is the obvious version and it is wrong twice over: it takes text
 * selection away from every error message and payload on the card, and it turns a
 * slightly-moved click on the mode toggle into a drag. Arming the card on the handle's
 * pointerdown is the other tempting version, and it races - the browser reads
 * `draggable` when the pointer crosses the drag threshold, which is not guaranteed to
 * be after React has re-rendered with the attribute set. Dragging *from* the button
 * has no such window; `setDragImage` puts the card itself under the cursor, so it still
 * looks like the card is what moved.
 *
 * **The drop target is a card, not a gap.** Dropping onto a card puts the dragged one
 * where that card was. Gap targets need twice the hit areas and a much more careful
 * indicator to be understood, and buy precision that reordering perhaps a dozen cards
 * does not need.
 *
 * **The handle is a real button with arrow keys.** Native drag and drop is unusable
 * without a mouse, so a feature built only on it is a feature some people cannot use at
 * all. Left/right move by one, up/down by a row, and the live region says where the card
 * landed - the same reordering, reachable from the keyboard.
 */

import { GripVertical } from 'lucide-react'
import { useId, useState } from 'react'
import type { ReactNode } from 'react'

import { cn } from '../lib/cn'

interface SortableGridProps {
  /** The ids to render, in their current order. */
  ids: string[]
  /** Drop `dragId` onto the position `overId` holds. */
  onMoveOnto: (dragId: string, overId: string) => void
  /** Nudge one card by `delta` places, from the keyboard. */
  onMoveBy: (id: string, delta: number) => void
  /**
   * How many cards sit in a row at the current breakpoint - the distance up/down arrows
   * move. Only has to be right for the layout the keyboard user is looking at; being
   * wrong at a narrow width costs them a slightly surprising jump, not correctness.
   */
  columns?: number
  className?: string
  /** A human name for one card, for the handle's label and the announcements. */
  label: (id: string) => string
  children: (id: string, handle: ReactNode) => ReactNode
}

export function SortableGrid({
  ids,
  onMoveOnto,
  onMoveBy,
  columns = 2,
  className,
  label,
  children,
}: SortableGridProps) {
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)
  const [announcement, setAnnouncement] = useState('')
  const liveId = useId()

  const finish = () => {
    setDraggingId(null)
    setOverId(null)
  }

  const move = (id: string, delta: number) => {
    const from = ids.indexOf(id)
    const to = Math.max(0, Math.min(ids.length - 1, from + delta))
    if (from === to) return
    onMoveBy(id, delta)
    setAnnouncement(`${label(id)} moved to position ${to + 1} of ${ids.length}.`)
  }

  return (
    <>
      <div className={className}>
        {ids.map((id) => {
          const isDragging = draggingId === id
          const isOver = overId === id && draggingId !== null && draggingId !== id

          const handle = (
            <button
              type="button"
              draggable
              onDragStart={(event) => {
                setDraggingId(id)
                // Firefox will not start a drag without data on the transfer.
                event.dataTransfer.setData('text/plain', id)
                event.dataTransfer.effectAllowed = 'move'
                // Drag the card, not the grip: the ghost under the cursor should be the
                // thing being moved, or the gesture reads as picking up a stray icon.
                const card = event.currentTarget.closest('[data-sortable-card]')
                if (card instanceof HTMLElement) {
                  event.dataTransfer.setDragImage(card, 24, 24)
                }
              }}
              onDragEnd={finish}
              onKeyDown={(event) => {
                const delta =
                  event.key === 'ArrowLeft'
                    ? -1
                    : event.key === 'ArrowRight'
                      ? 1
                      : event.key === 'ArrowUp'
                        ? -columns
                        : event.key === 'ArrowDown'
                          ? columns
                          : 0
                if (delta === 0) return
                event.preventDefault()
                move(id, delta)
              }}
              aria-label={`Reorder ${label(id)}. Use the arrow keys to move this card.`}
              aria-describedby={liveId}
              className={cn(
                'cursor-grab touch-none rounded-md border p-1 text-ink-muted transition-colors',
                'hover:bg-surface-sunken hover:text-ink-secondary',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1',
                isDragging && 'cursor-grabbing bg-surface-sunken text-ink-secondary',
              )}
            >
              <GripVertical size={13} aria-hidden />
            </button>
          )

          return (
            <div
              key={id}
              data-sortable-card={id}
              onDragOver={(event) => {
                if (draggingId === null || draggingId === id) return
                // The default is "reject the drop"; preventing it is what marks this a
                // valid target and lets onDrop fire at all.
                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
                setOverId(id)
              }}
              onDragLeave={() => setOverId((current) => (current === id ? null : current))}
              onDrop={(event) => {
                event.preventDefault()
                const dragId = draggingId ?? event.dataTransfer.getData('text/plain')
                if (dragId && dragId !== id) {
                  onMoveOnto(dragId, id)
                  setAnnouncement(`${label(dragId)} moved to ${label(id)}'s position.`)
                }
                finish()
              }}
              className={cn(
                'rounded-xl transition-[opacity,box-shadow] duration-150',
                // The card being carried fades; the one under the cursor is ringed, so
                // "this is where it will land" is legible without a moving placeholder.
                isDragging && 'opacity-40',
                isOver &&
                  'ring-2 ring-[var(--color-accent)] ring-offset-2 ring-offset-[var(--color-page)]',
              )}
            >
              {children(id, handle)}
            </div>
          )
        })}
      </div>

      {/* Where a keyboard move landed. Off-screen but not hidden - `display: none`
          would keep a screen reader from announcing it at all. */}
      <p id={liveId} aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </>
  )
}
